<template>
    <button
        :class="{
            'uik-btn__disabled': disabled,
            'uik-btn__base': true,
            'uik-btn__success': success,
            'uik-btn__primary': primary,
            'uik-btn__empty': empty,
            'uik-btn__transparent': transparent,
            'uik-btn__error': error,
            'uik-btn__dark': dark,
            'uik-btn__clear': clear,
            'uik-btn__xs': xs,
            'uik-btn__lg': lg,
            'uik-btn__isExpanded': isExpanded,
            'uik-btn__isLoading': isLoading,
            'uik-btn__noBorder': borderless,
            'uik-btn__iconOnly': iconOnly,
            'uik-btn__rounded': rounded
        }"
        :title="iconOnly ? text:''"
        :disabled="disabled"
        @click="$emit('click')"
    >
        <div v-if="isLoading" class="uik-btn__loading">
            <UikLoader
                :color="loaderColor ? loaderColor:presetLoaderColor"
                :size="loaderSize"
                :type="loaderType"/>
        </div>
        <span
            :class="{'uik-btn__content--reverse': iconPosition == 'right'}"
            class="uik-btn__content"
        >
            <Uikon v-if="icon" :class="{'uikon--iconOnly': iconOnly}">{{ icon }}</Uikon>
            <span v-if="text && !iconOnly">{{ text }}</span>
            <slot></slot>
        </span>
    </button>
</template>

<script>
export default {
    name: "UikButton",
    props: {
        // Component: { type: String, default: 'button' },
        disabled: { type: Boolean, default: false },
        success: { type: Boolean, default: false },
        primary: { type: Boolean, default: false },
        empty: { type: Boolean, default: false },
        transparent: { type: Boolean, default: false },
        error: { type: Boolean, default: false },
        dark: { type: Boolean, default: false },
        clear: { type: Boolean, default: false },
        xs: { type: Boolean, default: false },
        lg: { type: Boolean, default: false },
        isExpanded: { type: Boolean, default: false },
        isLoading: { type: Boolean, default: false },
        borderless: { type: Boolean, default: false },
        text: { type: String, default: "" },
        icon: { type: String, default: "" },
        iconOnly: { type: Boolean, default: false },
        iconPosition: { type: String, default: "" },
        rounded: { type: Boolean, default: false },
        loaderType: { type: String, default: "dots" },
        loaderSize: { type: Number, default: 0.36 },
        loaderColor: { type: String, default: "" }
    },
    computed: {
        presetLoaderColor () {
            if (
                this.success ||
                this.primary ||
                this.error ||
                this.dark
            ) {
                return "white"
            } else if (this.empty) {
                return "primary"
            } else if (this.clear) {
                return "textSecondary"
            } else if (this.disabled) {
                return "white"
            } else {
                return "textMain"
            }
        }
    }
}
</script>

<style lang="scss">
a.uik-btn__base:hover {
    text-decoration: none !important;
}

.uik-btn__base {
    display: inline-flex;
    align-items: center;
    text-align: center;
    white-space: nowrap;
    vertical-align: middle;
    user-select: none;
    height: 38px;
    padding: 0 18px;
    transition: all 0.1s ease-in-out 0s;
    font-size: 0.875rem;
    font-weight: 400;
    position: relative;
    outline: none;
    i {
        opacity: 0.8;
    }
    &.uik-btn__hasIcon {
        padding-left: 40px;
        .uik-btn__iconWrapper {
            position: absolute;
            top: 50%;
            left: 12px;
            transform: translateY(-50%);
            font-size: 18px;
        }
    }
    &.uik-btn__iconRight {
        padding-right: 40px;
        padding-left: 18px;
        .uik-btn__iconWrapper {
            left: auto;
            right: 12px;
        }
    }
    &.uik-btn__iconOnly {
        padding: 0;
        justify-content: center;
        flex-basis: 38px;
        width: 38px;
        flex-shrink: 0;
        .uik-btn__iconWrapper {
            left: 50%;
            transform: translateX(-50%) translateY(-50%);
        }
    }
    &.uik-btn__isExpanded {
        width: 100%;
        &.uik-btn__hasIcon {
            padding-left: 40px;
            padding-right: 40px;
        }
    }
    .uik-btn__content {
        flex-grow: 1;
        height: 100%;
        display: flex;
        flex-flow: row nowrap;
        align-items: center;
        justify-content: center;
        .uikon {
            position: relative;
            margin-right: 5px;
            right: 2px;
        }
        .uikon--iconOnly {
            margin-right: 0;
            right: auto;
        }
    }
    .uik-btn__content--reverse {
        flex-direction: row-reverse;
        .uikon {
            margin-right: 0;
            margin-left: 5px;
            right: auto;
            left: 2px;
        }
    }
    & > * {
        transition: 0.1s all;
    }
    &.uik-btn__isLoading {
        & > * {
            opacity: 0;
        }
        & > .uik-btn__loading {
            height: 100%;
            width: 100%;
            position: absolute;
            display: flex;
            align-items: center;
            justify-content: center;
            left: 0;
            opacity: 1;
        }
    }
    &.uik-btn__isLoading.uik-btn__empty {
        &:hover {
            background: transparent !important;
        }
        &:active {
            background: transparent !important;
        }
        &:active {
            background: transparent !important;
        }
    }
    &.uik-btn__xs {
        height: 28px;
        padding: 0 12px;
        font-size: 12px;
    }
    &.uik-btn__lg {
        height: 48px;
        padding: 0 40px;
        font-size: 16px;
    }
    &:not(:disabled) {
        cursor: pointer;
    }
    &:active {
        outline: none;
    }
    &:active {
        border: 1px solid #eee;
        box-shadow: none;
        transform: translateY(0px);
    }
    /*Basic Appearence*/
    background: #ffffff;
    background-image: linear-gradient(0deg, #f6f7f9 0%, #ffffff 100%);
    border: 1px solid $borderColorDarker;
    box-shadow: 0 1px 1px 0 rgba(22, 29, 37, 0.05),
        inset 0 2px 0 0 rgba(255, 255, 255, 0.05);
    color: $textMain;
    border-radius: 4px;
    &:hover {
        background-image: linear-gradient(0deg, #f6f7f9 0%, #ffffff 100%);
        border: 1px solid #cacedb;
        box-shadow: 0 1px 2px 0 rgba(22, 29, 37, 0.1),
            inset 0 2px 0 0 rgba(255, 255, 255, 0.05);
    }
    &:active {
        background-image: linear-gradient(0deg, #f6f7f9 0%, #ffffff 100%);
        border: 1px solid #bdc2d1;
        box-shadow: 0 0 1px 0 rgba(22, 29, 37, 0.1),
            inset 0 2px 0 0 rgba(255, 255, 255, 0.05);
    }
    &.uik-btn__noBorder {
        border: none !important;
        background: white;
        box-shadow: none;
        &:hover {
            box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.13);
        }
        &:active {
            box-shadow: 0 0 2px 0 rgba(0, 0, 0, 0.1);
        }
    }
    /*Disabled*/
    &.uik-btn__disabled {
        pointer-events: none;
    }
    /*Colors*/
    &.uik-btn__transparent {
        background: transparent;
        border: 1px solid #e2e5ed;
        box-shadow: none;
        &:hover {
            border: 1px solid #cacedb;
        }
        &:active {
            border: 1px solid #bdc2d1;
        }
    }
    &.uik-btn__dark {
        color: white;
        background: rgba($textMain, 0.6);
        border: 1px solid rgba($textMain, 0.8);
        box-shadow: 0 1px 1px 0 rgba(28, 28, 28, 0.1),
            inset 0 2px 0 0 rgba(255, 255, 255, 0.06);
        transition: none;
        &:hover {
            background: rgba($textMain, 0.7);
            border: 1px solid rgba($textMain, 0.9);
            box-shadow: 0 1px 1px 0 rgba(28, 28, 28, 0.18),
                inset 0 2px 0 0 rgba(255, 255, 255, 0.06);
            &.uik-btn__noBorder {
                box-shadow: 0 1px 1px 0 rgba(28, 28, 28, 0.18);
            }
        }
        &:active {
            background: rgba($textMain, 0.75);
            border: 1px solid rgba($textMain, 0.9);
            box-shadow: 0 1px 1px 0 rgba(28, 28, 28, 0.1),
                inset 0 2px 0 0 rgba(255, 255, 255, 0.06);
            &.uik-btn__noBorder {
                box-shadow: 0 1px 1px 0 rgba(28, 28, 28, 0.1);
            }
        }
    }
    &.uik-btn__success {
        color: white;
        background: $success;
        background-image: linear-gradient(0deg, $success 2%, lighten($success, 2%) 98%);
        border: 1px solid darken($success, 3%);
        box-shadow: 0 1px 1px 0 rgba(28, 28, 28, 0.1),
            inset 0 2px 0 0 rgba(255, 255, 255, 0.08);
        &:hover {
            background-image: linear-gradient(0deg, lighten($success, 3%) 0%, lighten($success, 5%) 100%);
            border: 1px solid $success;
        }
        &:active {
            background: $success;
            background-image: linear-gradient(0deg, $success 2%, lighten($success, 2%) 98%);
            border: 1px solid darken($success, 3%);
        }
    }
    &.uik-btn__error {
        color: white;
        background: $error;
        background-image: linear-gradient(0deg, $error 2%, lighten($error, 2%) 98%);
        border: 1px solid darken($error, 3%);
        box-shadow: 0 1px 1px 0 rgba(28, 28, 28, 0.1),
            inset 0 2px 0 0 rgba(255, 255, 255, 0.08);
        &:hover {
            background-image: linear-gradient(0deg, lighten($error, 3%) 0%, lighten($error, 5%) 100%);
            border: 1px solid $error;
        }
        &:active {
            background: $error;
            background-image: linear-gradient(0deg, $error 2%, lighten($error, 2%) 98%);
            border: 1px solid darken($error, 3%);
        }
    }
    &.uik-btn__primary {
        color: white;
        background: $primary;
        background-image: linear-gradient(0deg, $primary 2%, lighten($primary, 2%) 98%);
        border: 1px solid darken($primary, 3%);
        box-shadow: 0 1px 1px 0 rgba(28, 28, 28, 0.1),
            inset 0 2px 0 0 rgba(255, 255, 255, 0.08);
        &:hover {
            background-image: linear-gradient(0deg, lighten($primary, 3%) 0%, lighten($primary, 5%) 100%);
            border: 1px solid $primary;
        }
        &:active {
            background: $primary;
            background-image: linear-gradient(0deg, $primary 2%, lighten($primary, 2%) 98%);
            border: 1px solid darken($primary, 3%);
        }
    }
    &.uik-btn__empty {
        color: $primary;
        background: transparent;
        border: 1px solid darken($primary, 3%);
        box-shadow: none;
        transition: none !important;
        &.uik-btn__icon {
            color: $primary;
        }
        &:hover {
            color: white;
            background-image: linear-gradient(0deg, lighten($primary, 3%) 0%, lighten($primary, 5%) 100%);
            border: 1px solid $primary;
        }
        &:active {
            color: white;
            background: $primary;
            background-image: linear-gradient(0deg, $primary 2%, lighten($primary, 2%) 98%);
            border: 1px solid darken($primary, 3%);
        }
    }
    &.uik-btn__iconOnly {
        padding: 0 10px;
    }
    &.uik-btn__iconOnly.uik-btn__primary.uik-btn__clear {
        background: transparent !important;
        border: none !important;
        .uikon {
            color: $primary;
        }
    }
    &.uik-btn__rounded {
        border-radius: 999px;
    }
    &:disabled {
        &.uik-btn__icon {
            opacity: 0.3;
        }
        background: #747578;
        border: 1px solid #696a6e;
        box-shadow: inset 0 2px 0 0 rgba(255, 255, 255, 0.06);
        border-radius: 4px;
        color: $textSecondary;
    } //icon inisde the button
    //Button without backround and without hover or effect
    &.uik-btn__clear {
        background: transparent;
        border: none;
        outline: none;
        box-shadow: none;
        color: $textSecondary;
        &:hover {
            color: $textBase;
        }
    }
}

/* Necessary if icon is present in <a> element
// otherwise it's not aligned to the text */

a.uik-btn__commonButton * {
    vertical-align: middle;
}

a.uik-btn__commonButton {
    text-decoration: none;
}
</style>
